import { useEffect, useRef, useState, useCallback } from 'react';
import * as THREE from 'three';
import { lineOfSight } from '@ydrive/sparky';
import { projectPointToLineXZ, projectToScreen, screenToWorldOnPlane, type ScreenPos } from '../utils/measureUtils';

const SIGN_EYE_HEIGHT = 3.5; // eye at sign face center (~85% of the 4.1m model height)
const CAR_EYE_HEIGHT  = 0.34;

interface Props {
  viewerRef:        React.RefObject<any>;
  signRootRef:      React.RefObject<THREE.Object3D | null>;
  signEyeRef:       React.RefObject<THREE.Vector3 | null>;
  carRootRef:       React.RefObject<THREE.Object3D | null>;
  /** Point C — user-placed road construction point for the B→C→A triangle. */
  cPointRef?:       React.RefObject<THREE.Vector3 | null>;
  placementVersion: number;
  autoCompute?:     boolean;
  drivePath?:       Array<{ x: number; y: number; z: number }>;
  startDriveRef?:   React.MutableRefObject<(() => void) | null>;
  onLosResult:      (result: { visible: boolean; distance?: number } | null) => void;
}

interface LOSResult {
  visible:   boolean;
  blockedAt: THREE.Vector3 | null;
}

export default function LineOfSightOverlay({
  viewerRef, signRootRef, signEyeRef, carRootRef, cPointRef,
  placementVersion, autoCompute, drivePath, startDriveRef, onLosResult,
}: Props) {
  const [screenSign,    setScreenSign]    = useState<ScreenPos | null>(null);
  const [screenSignEye, setScreenSignEye] = useState<ScreenPos | null>(null);
  const [screenCar,     setScreenCar]     = useState<ScreenPos | null>(null);
  const [screenCarEye,  setScreenCarEye]  = useState<ScreenPos | null>(null);
  const [screenC,       setScreenC]       = useState<ScreenPos | null>(null);
  const [screenCEye,    setScreenCEye]    = useState<ScreenPos | null>(null);
  const [screenBlocked, setScreenBlocked] = useState<ScreenPos | null>(null);
  const [losResult,     setLosResult]     = useState<LOSResult | null>(null);
  const [dragging,      setDragging]      = useState<'car' | 'sign' | null>(null);
  const [showDrivePath, setShowDrivePath] = useState(false);
  const rafRef = useRef<number>(0);
  const driveRafRef = useRef<number>(0);
  const drivingRef = useRef(false);

  const bothPresent = !!(signRootRef.current && carRootRef.current);

  // ── LOS computation ────────────────────────────────────────────────────────
  const computeNow = useCallback(() => {
    const viewer    = viewerRef.current;
    const splatMesh = viewer?.splatMesh;
    const sign      = signRootRef.current;
    const car       = carRootRef.current;
    const cPt       = cPointRef?.current ?? null;
    if (!splatMesh || !sign || !car) {
      console.log('[LOS] computeNow early exit — splatMesh:', !!splatMesh, 'sign:', !!sign, 'car:', !!car);
      return null;
    }

    // LOS is always evaluated from the sign (A) to the car (B). C is only
    // auxiliary road-construction geometry and must never change visibility.
    const from = signEyeRef.current?.clone()
      ?? new THREE.Vector3(sign.position.x, sign.position.y + SIGN_EYE_HEIGHT, sign.position.z);

    const to = new THREE.Vector3(car.position.x, car.position.y + CAR_EYE_HEIGHT, car.position.z);

    console.log('[LOS] computing — from:', from.toArray().map(v => v.toFixed(2)), 'to:', to.toArray().map(v => v.toFixed(2)));
    const result = lineOfSight(splatMesh, from, to);
    console.log('[LOS] result:', result.visible, 'blockedAt:', result.blockedAt?.toArray().map((v: number) => v.toFixed(2)));

    const blocked = result.blockedAt ?? null;
    setLosResult({ visible: result.visible, blockedAt: blocked });

    const canvas = viewer?.renderer?.domElement as HTMLCanvasElement | undefined;
    const camera = viewer?.camera as THREE.Camera | undefined;
    if (blocked && canvas && camera) {
      setScreenBlocked(projectToScreen({ x: blocked.x, y: blocked.y, z: blocked.z }, camera, canvas));
    } else {
      setScreenBlocked(null);
    }
    return result.visible;
  }, [viewerRef, signRootRef, signEyeRef, carRootRef, cPointRef]);

  const startDrive = useCallback(() => {
    if (drivingRef.current || !carRootRef.current) return;
    const sign = signRootRef.current;
    if (!sign) return;
    const directionPoint = cPointRef?.current;
    const computedC = directionPoint
      ? projectPointToLineXZ(sign.position, carRootRef.current.position, directionPoint, false)?.projectedPoint
      : null;
    const a = drivePath && drivePath.length >= 2
      ? drivePath[0]
      : carRootRef.current.position.clone();
    const b = drivePath && drivePath.length >= 2
      ? drivePath[drivePath.length - 1]
      : computedC;
    if (!b) return;
    const signX = cPointRef?.current?.x ?? sign.position.x;
    const signZ = cPointRef?.current?.z ?? sign.position.z;
    const distanceToSign = (p: { x: number; z: number }) => Math.hypot(p.x - signX, p.z - signZ);
    const start = drivePath && drivePath.length >= 2 && distanceToSign(a) < distanceToSign(b) ? b : a;
    const target = start === a ? b : a;
    const dx = target.x - start.x;
    const dz = target.z - start.z;
    const length = Math.hypot(dx, dz);
    if (length < 0.1) return;
    const speed = 2.0;
    let travelled = 0;
    drivingRef.current = true;
    setShowDrivePath(true);
    const step = () => {
      if (!drivingRef.current || !carRootRef.current) return;
      travelled = Math.min(length, travelled + speed);
      const t = travelled / length;
      carRootRef.current.position.x = start.x + dx * t;
      carRootRef.current.position.z = start.z + dz * t;
      const visible = computeNow();
      if (visible === true || travelled >= length) {
        drivingRef.current = false;
        return;
      }
      driveRafRef.current = requestAnimationFrame(step);
    };
    driveRafRef.current = requestAnimationFrame(step);
  }, [carRootRef, cPointRef, drivePath, signRootRef, computeNow]);

  useEffect(() => {
    if (startDriveRef) startDriveRef.current = startDrive;
    return () => {
      drivingRef.current = false;
      cancelAnimationFrame(driveRafRef.current);
      if (startDriveRef) startDriveRef.current = null;
    };
  }, [startDrive, startDriveRef]);

  // Recompute whenever a model is newly placed or C changes.
  useEffect(() => {
    setScreenBlocked(null);
    if (signRootRef.current && carRootRef.current) {
      setTimeout(computeNow, 80);
    } else {
      setLosResult(null);
    }
  }, [placementVersion]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-compute triggered from parent (agent-placed models).
  useEffect(() => {
    if (autoCompute && bothPresent) computeNow();
  }, [autoCompute, bothPresent, computeNow]);

  // Emit LOS result to parent panel.
  useEffect(() => {
    if (losResult) {
      const sign = signRootRef.current;
      const car = carRootRef.current;
      const distance = sign && car
        ? sign.position.distanceTo(car.position)
        : undefined;
      onLosResult({ visible: losResult.visible, distance });
    } else {
      onLosResult(null);
    }
  }, [losResult, onLosResult]);

  // ── RAF loop — project world positions to screen every frame ───────────────
  useEffect(() => {
    const tick = () => {
      const viewer = viewerRef.current;
      const canvas = viewer?.renderer?.domElement as HTMLCanvasElement | undefined;
      const camera = viewer?.camera as THREE.Camera | undefined;
      const sign   = signRootRef.current;
      const car    = carRootRef.current;
      const cPt    = cPointRef?.current ?? null;

      if (canvas && camera) {
        setScreenSign(sign ? projectToScreen(sign.position, camera, canvas) : null);
        const eyeWorld = signEyeRef.current
          ?? (sign ? new THREE.Vector3(sign.position.x, sign.position.y + SIGN_EYE_HEIGHT, sign.position.z) : null);
        setScreenSignEye(eyeWorld ? projectToScreen(eyeWorld, camera, canvas) : null);
        setScreenCar(car ? projectToScreen(car.position, camera, canvas) : null);
        setScreenCarEye(car
          ? projectToScreen(new THREE.Vector3(car.position.x, car.position.y + CAR_EYE_HEIGHT, car.position.z), camera, canvas)
          : null);
        if (cPt) {
          const cScreen = projectToScreen(cPt, camera, canvas);
          setScreenC(cScreen);
          setScreenCEye(cScreen);
        } else {
          setScreenC(null);
          setScreenCEye(null);
        }
      }

      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [viewerRef, signRootRef, signEyeRef, carRootRef, cPointRef]);

  // ── Drag handler — move car (B) or point C on the horizontal plane ─────────
  useEffect(() => {
    if (!dragging) return;

    const viewer = viewerRef.current;
    const canvas = viewer?.renderer?.domElement as HTMLCanvasElement | undefined;
    const camera = viewer?.camera as THREE.Camera | undefined;

    const onPointerMove = (e: PointerEvent) => {
      if (!canvas || !camera) return;

      if (dragging === 'car' && carRootRef.current) {
        const anchorY = carRootRef.current.position.y;
        const hit = screenToWorldOnPlane(e.clientX, e.clientY, camera, canvas, anchorY);
        if (!hit) return;
        carRootRef.current.position.x = hit.x;
        carRootRef.current.position.z = hit.z;
        // Keep the perpendicular foot C visually attached to the moving car.
        const sign = signRootRef.current;
        const directionPoint = cPointRef?.current;
        if (sign && directionPoint) {
          const projected = projectPointToLineXZ(sign.position, carRootRef.current.position, directionPoint, false)?.projectedPoint;
          if (projected) {
            const cScreen = projectToScreen(projected, camera, canvas);
            setScreenC(cScreen);
            setScreenCEye(cScreen);
          }
        }
        // No LOS recompute during drag — fires on pointerup instead.
      }

      if (dragging === 'sign' && signRootRef.current) {
        const anchorY = signRootRef.current.position.y;
        const hit = screenToWorldOnPlane(e.clientX, e.clientY, camera, canvas, anchorY);
        if (!hit) return;
        signRootRef.current.position.x = hit.x;
        signRootRef.current.position.z = hit.z;
        // Keep signEyeRef in sync so LOS ray fires from the right eye position.
        if (signEyeRef.current) {
          (signEyeRef as React.MutableRefObject<THREE.Vector3>).current.x = hit.x;
          (signEyeRef as React.MutableRefObject<THREE.Vector3>).current.z = hit.z;
        }
      }
    };

    const onPointerUp = () => {
      const wasDragging = dragging;
      setDragging(null);
      // Wait one RAF tick so screen positions are updated before recomputing.
      if (wasDragging === 'car' || wasDragging === 'sign') {
        requestAnimationFrame(computeNow);
      }
    };

    window.addEventListener('pointermove',   onPointerMove);
    window.addEventListener('pointerup',     onPointerUp);
    window.addEventListener('pointercancel', onPointerUp);
    return () => {
      window.removeEventListener('pointermove',   onPointerMove);
      window.removeEventListener('pointerup',     onPointerUp);
      window.removeEventListener('pointercancel', onPointerUp);
    };
  }, [dragging, viewerRef, signRootRef, signEyeRef, carRootRef, computeNow]);

  // ── Derived display values ─────────────────────────────────────────────────
  const lineColor = losResult?.visible ? '#00e676' : '#ff1744';

  const losLineStart = screenSignEye;

  const blockerRef = screenSignEye;
  const blockerDistFromEye = (() => {
    if (!screenBlocked || !blockerRef) return 0;
    const dx = screenBlocked.x - blockerRef.x;
    const dy = screenBlocked.y - blockerRef.y;
    return Math.sqrt(dx * dx + dy * dy);
  })();
  const showBlockerDot =
    losResult != null && !losResult.visible &&
    screenBlocked?.visible === true && blockerDistFromEye > 30;

  return (
    <>
      {/* ── SVG overlay ──────────────────────────────────────────────────────── */}
      <svg className="fixed inset-0 z-[1000]" style={{ width: '100vw', height: '100vh', pointerEvents: 'none' }}>

        {/* Stem: sign base → sign eye (dashed, always shown) */}
        {screenSign?.visible && screenSignEye?.visible && (
          <line
            x1={screenSign.x} y1={screenSign.y} x2={screenSignEye.x} y2={screenSignEye.y}
            stroke="rgba(255,255,255,0.25)" strokeWidth={1.5} strokeDasharray="3 3"
          />
        )}

        {/* Road construction triangle: B → C → A — only shown after drive is initiated */}
        {showDrivePath && screenSignEye?.visible && screenCar?.visible && screenCEye?.visible && (
          <line
            x1={screenCar?.x} y1={screenCar?.y} x2={screenCEye.x} y2={screenCEye.y}
            stroke="rgba(251,191,36,0.5)" strokeWidth={1.5} strokeDasharray="4 4"
          />
        )}
        {showDrivePath && screenSign?.visible && screenCEye?.visible && (
          <line
            x1={screenCEye.x} y1={screenCEye.y} x2={screenSign.x} y2={screenSign.y}
            stroke="rgba(251,191,36,0.5)" strokeWidth={1.5} strokeDasharray="4 4"
          />
        )}

        {/* LOS line: C eye (or A eye) → car eye — SVG clips naturally at viewport edge */}
        {!dragging && losResult && losLineStart != null && screenCarEye != null && (
          <line
            x1={losLineStart.x} y1={losLineStart.y} x2={screenCarEye.x} y2={screenCarEye.y}
            stroke={lineColor} strokeWidth={3} strokeLinecap="round"
          />
        )}

        {/* Blocker dot */}
        {!dragging && showBlockerDot && (
          <circle cx={screenBlocked!.x} cy={screenBlocked!.y} r={6} fill="#ff1744" stroke="white" strokeWidth={2} />
        )}

        {/* Sign marker A (static) */}
        {screenSign?.visible && signRootRef.current && (
          <g style={{ pointerEvents: 'none' }}>
            <circle cx={screenSign.x} cy={screenSign.y} r={9} fill="white" opacity={0.75} />
            <text x={screenSign.x} y={screenSign.y + 3.5} textAnchor="middle" fill="#111" fontSize={8} fontWeight="bold">A</text>
          </g>
        )}

        {/* Point C marker — only shown after drive is initiated */}
        {showDrivePath && screenC?.visible && cPointRef?.current && (
          <circle cx={screenC.x} cy={screenC.y} r={8} fill="#f59e0b" opacity={0.85} style={{ pointerEvents: 'none' }} />
        )}

        {/* Car marker B */}
        {screenCar?.visible && carRootRef.current && (
          <g style={{ pointerEvents: 'none' }}>
            <circle cx={screenCar.x} cy={screenCar.y} r={9} fill={losResult ? lineColor : '#888'} opacity={0.92} />
            <text x={screenCar.x} y={screenCar.y + 3.5} textAnchor="middle" fill="white" fontSize={8} fontWeight="bold">B</text>
          </g>
        )}
      </svg>

      {/* ── Drag handle for A (sign) ─────────────────────────────────────────── */}
      {screenSign?.visible && signRootRef.current && (
        <div
          className="fixed z-[1003]"
          style={{
            left: screenSign.x - 18, top: screenSign.y - 18,
            width: 36, height: 36, borderRadius: '50%',
            background: 'transparent',
            cursor: dragging === 'sign' ? 'grabbing' : 'grab',
          }}
          onPointerDown={e => {
            e.stopPropagation();
            e.preventDefault();
            (e.currentTarget as HTMLDivElement).setPointerCapture(e.pointerId);
            setDragging('sign');
          }}
        />
      )}

      {/* ── Drag handle for B (car) ──────────────────────────────────────────── */}
      {screenCar?.visible && carRootRef.current && (
        <div
          className="fixed z-[1003]"
          style={{
            left: screenCar.x - 100, top: screenCar.y - 150,
            width: 200, height: 200, borderRadius: '50%',
            background: 'transparent',
            cursor: dragging === 'car' ? 'grabbing' : 'grab',
          }}
          onPointerDown={e => {
            e.stopPropagation();
            e.preventDefault();
            (e.currentTarget as HTMLDivElement).setPointerCapture(e.pointerId);
            setDragging('car');
          }}
        />
      )}

    </>
  );
}
